import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import vixenLogo from '../assets/Vixen_Hollow_icon_white.png';

// REMOVED NavbarProps interface as URLs are now from environment variables
// interface NavbarProps {
//   employeeUrl?: string; 
//   boardUrl?: string;
//   eventsUrl?: string;
//   lessonsUrl?: string;
// }

// Component no longer accepts URL props
const Navbar: React.FC = () => {
  // Read URLs from environment variables with fallbacks
  // const homeNavUrl = process.env.REACT_APP_NAV_HOME_URL || 'https://my.vixenhollow.org';
  // const employeeNavUrl = process.env.REACT_APP_NAV_EMPLOYEE_URL || '#';
  // const boardNavUrl = process.env.REACT_APP_NAV_BOARD_URL || '#';
  
  // --- Hardcode URLs --- 
  const homeNavUrl = 'https://my.vixenhollow.org';
  const employeeNavUrl = 'https://employee.vixenhollow.org';
  const boardNavUrl = 'https://my.vixenhollow.org';
  const eventsNavUrl = '#'; // Keep as placeholder
  const lessonsNavUrl = '#'; // Keep as placeholder
  // --- End Hardcoding ---

  const location = useLocation();
  const { user, logout, loading, isAdmin, login } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null); // Add ref for mobile menu
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null); // Ref for the button
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Initialize based on current width

  // Debug user and admin status
  console.log('✅ Navbar: Current user:', user);
  console.log('✅ Navbar: Is admin?', isAdmin);
  // Log the URLs being used
  console.log('✅ Navbar: Home URL:', homeNavUrl);
  console.log('✅ Navbar: Employee URL:', employeeNavUrl);
  console.log('✅ Navbar: Board URL:', boardNavUrl);
  // console.log('✅ Navbar: Events URL:', eventsNavUrl); // Keep commented
  // console.log('✅ Navbar: Lessons URL:', lessonsNavUrl); // Keep commented

  const handleLogout = () => {
    // Close dropdown first if open
    setProfileDropdownOpen(false);
    logout();
    // Navigation is handled within the logout function in AuthContext
    // navigate('/login'); 
  };
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Ignore clicks on the toggle button itself
      if (mobileMenuButtonRef.current && mobileMenuButtonRef.current.contains(event.target as Node)) {
        return;
      }
      
      // Close profile dropdown if click is outside its ref
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
      // Close mobile menu if click is outside its ref AND it's open
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]); // Keep dependency

  // Add effect to check window size for mobile state
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Tailwind's 'md' breakpoint
    };
    checkMobile(); // Initial check
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (loading) {
    // Simplified loading state
    return (
      <div className="fixed top-0 left-0 right-0 z-[1500]">
        <nav className="flex items-center justify-between px-5 py-[10px] bg-[#2c3e50] text-white h-[64px] box-border">
          <div className="flex items-center">
            <img src={vixenLogo} alt="Vixen Hollow" className="h-12 w-auto" />
          </div>
          <div>Loading...</div>
        </nav>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[1500]">
      {/* Top Navbar */}
      <nav className="flex items-center justify-between px-5 py-[10px] bg-[#2c3e50] text-white h-[64px] box-border w-full">
        {/* Left side: Logo and Top Links */}
        <div className="flex items-center">
          <Link to={homeNavUrl} className="flex items-center mr-6">
            <img src={vixenLogo} alt="Vixen Hollow" className="h-12 w-auto" />
          </Link>
          
          {/* Top Level Links (Hidden on mobile) */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Use the employeeNavUrl from env var */}
            <Link 
              to={employeeNavUrl} 
              className={`text-lg px-3 py-1 rounded ${location.pathname.startsWith(employeeNavUrl) ? 'bg-[#415972] text-white' : 'text-white hover:bg-[#415972]'}`}
            >
              Employee
            </Link>
            {/* Use the boardNavUrl from env var */}
            {/* TEMPORARILY HARDCODE URL for debugging */}
            <Link to="https://my.vixenhollow.org" className="text-lg px-3 py-1 rounded text-white hover:bg-[#415972]">
            {/* <Link to={boardNavUrl} className="text-lg px-3 py-1 rounded text-white hover:bg-[#415972]"> */}
              Board
            </Link>
            {/* Events Link - points to # for now */}
            <Link to={eventsNavUrl} className="text-lg px-3 py-1 rounded text-white hover:bg-[#415972] flex items-center">
              Events <span className="text-xs text-gray-400 ml-1">(coming soon)</span>
            </Link>
            {/* Lessons Link - points to # for now */}
            <Link to={lessonsNavUrl} className="text-lg px-3 py-1 rounded text-white hover:bg-[#415972] flex items-center">
              Lessons <span className="text-xs text-gray-400 ml-1">(coming soon)</span>
            </Link>
          </div>
        </div>
        
        {/* Right side: Mobile Menu Button and Profile Dropdown */}
        <div className="flex items-center">
          {/* Mobile menu button (visible only on mobile) */}
          <button 
            ref={mobileMenuButtonRef}
            className="md:hidden px-2 py-1 mr-2 text-white" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
             </svg>
          </button>
          
          {/* Profile Icon/Dropdown (or Public View) */}
          {user ? (
            <>
              {/* Container for Button & *Desktop* Dropdown only */}
              <div className="relative mr-2 md:mr-0" ref={dropdownRef}> 
                {/* Profile Button */}
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center justify-center h-10 w-10 bg-gray-700 rounded-full hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                  aria-label="User menu"
                  aria-haspopup="true"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
                
                {/* Desktop Dropdown (Inside Relative Container) */} 
                {profileDropdownOpen && (
                  <div
                    className={`hidden md:block origin-top-right absolute right-2 mt-2 w-56 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[2000]`}
                    role="menu" 
                    aria-orientation="vertical" 
                    aria-labelledby="user-menu-button" 
                    tabIndex={-1}
                  >
                    {/* Desktop Content */} 
                    <div className="px-4 py-3 border-b border-gray-200" role="none">
                      <p className="text-sm text-gray-500" role="none">Signed in as</p>
                      <p className="text-sm font-medium text-gray-900 truncate" role="none">
                        {user.first_name} {user.last_name}
                        {isAdmin && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">Admin</span>
                        )}
                      </p>
                    </div>
                    <div className="py-1" role="none">
                      {/* Add Disabled Profile Button for Desktop */}
                      <button
                        disabled
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-not-allowed" 
                        role="menuitem"
                        tabIndex={-1}
                      >
                        Profile <span className="text-xs text-gray-400 ml-1">(coming soon)</span>
                      </button>
                      {/* Existing Logout Button */}
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700" 
                        role="menuitem" 
                        tabIndex={-1}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Dropdown (OUTSIDE Relative Container) */} 
              {profileDropdownOpen && (
                <div 
                  className={`block md:hidden absolute top-[64px] left-0 right-0 z-[1999] bg-[#2c3e50] shadow-md border-t border-gray-700 text-white`}
                  role="menu" 
                  aria-orientation="vertical" 
                  aria-labelledby="user-menu-button" 
                >
                  {/* Mobile Content - Single Section, Standardized Styling */}
                  <div className="px-4 py-2 space-y-1" role="none"> {/* Match hamburger container */} 
                    {/* User Info */} 
                    <div>
                      <p className="text-sm text-gray-300" role="none">Signed in as</p>
                      <p className="text-base font-medium text-white truncate" role="none">
                        {user.first_name} {user.last_name}
                        {isAdmin && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-200 text-blue-900 rounded-full">Admin</span>
                        )}
                      </p>
                    </div>
                    
                    {/* Profile Button (Coming Soon) */}
                     <button
                       disabled
                       className="block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal text-white cursor-not-allowed"
                       role="menuitem"
                     >
                       Profile <span className="text-xs text-gray-400 ml-1">(coming soon)</span>
                     </button>
                     
                    {/* Logout Button */}
                     <button
                       onClick={() => { handleLogout(); setProfileDropdownOpen(false); }}
                       className="block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal text-white hover:bg-[#415972]"
                       role="menuitem"
                     >
                       Logout
                     </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <button 
              onClick={login} 
              className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white text-lg"
            >
              Sign In
            </button>
          )}
        </div>
      </nav>

      {/* Secondary Navbar - Application Specific Links */}
      <div className="flex items-center px-5 py-2 bg-[#415972] text-white w-full shadow-md secondary-nav z-[1000]">
        <div className="hidden md:flex space-x-4">
          <Link
            to="/public-schedule"
            className={`px-3 py-1 rounded hover:bg-[#2c3e50] text-lg ${location.pathname === '/public-schedule' ? 'bg-[#2c3e50]' : ''}`}
          >
            Public Schedule
          </Link>
          
          {user && (
            <>
              <Link 
                to="/myshifts" 
                className={`px-3 py-1 rounded hover:bg-[#2c3e50] text-lg ${location.pathname.startsWith('/myshifts') ? 'bg-[#2c3e50]' : ''}`}
              >
                My Shifts
              </Link>
            
              {isAdmin && (
                <Link 
                  to="/reports" 
                  className={`px-3 py-1 rounded hover:bg-[#2c3e50] text-lg ${location.pathname.startsWith('/reports') ? 'bg-[#2c3e50]' : ''}`}
                >
                  Reports
                </Link>
              )}
            </>
          )}
        </div>
        {/* Placeholder for mobile view of secondary links if needed, or handled by main mobile menu */}
      </div>

      {/* Mobile Menu - Dropdown from Hamburger */}
      {mobileMenuOpen && (
        <div 
          ref={mobileMenuRef}
          className="md:hidden absolute top-[64px] left-0 w-full bg-[#2c3e50] z-[1999] border-t border-[#34495e] shadow-md text-white"
        >
          {/* Section 1: Top Level Links - Standardize Container & Links */}
          <div className="px-4 py-2 space-y-1"> {/* Match button container */} 
            <Link
              to={employeeNavUrl}
              className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal ${location.pathname.startsWith(employeeNavUrl) ? 'bg-[#415972]' : 'hover:bg-[#415972]'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Employee
            </Link>
            {/* TEMPORARILY HARDCODE URL for debugging */}
            <Link
              to="https://my.vixenhollow.org"
              className="block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal hover:bg-[#415972]"
              onClick={() => setMobileMenuOpen(false)}
            >
            {/* <Link
              to={boardNavUrl}
              className="block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal hover:bg-[#415972]"
              onClick={() => setMobileMenuOpen(false)}
            > */}
              Board
            </Link>
            {/* Events link - points to # */}
            <Link
              to={eventsNavUrl} // Points to '#'
              className="block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal hover:bg-[#415972]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Events <span className="text-xs text-gray-400 ml-1">(coming soon)</span>
            </Link>
            {/* Lessons link - points to # */}
            <Link
              to={lessonsNavUrl} // Points to '#'
              className="block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal hover:bg-[#415972]"
              onClick={() => setMobileMenuOpen(false)}
            >
              Lessons <span className="text-xs text-gray-400 ml-1">(coming soon)</span>
            </Link>
          </div>
          
          {/* Divider - make more visible */}
          <div className="border-t border-gray-500 my-1"></div>

          {/* Section 2: App Specific Links - Standardize Container & Links */}
          <div className="px-4 py-2 space-y-1"> {/* Match button container */} 
            <Link
              to="/public-schedule"
              className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal ${location.pathname === '/public-schedule' ? 'bg-[#415972]' : 'hover:bg-[#415972]'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Public Schedule
            </Link>
            {user && (
              <>
                <Link 
                  to="/myshifts" 
                  className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal ${location.pathname.startsWith('/myshifts') ? 'bg-[#415972]' : 'hover:bg-[#415972]'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Shifts
                </Link>
                {isAdmin && (
                  <Link 
                    to="/reports" 
                    className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium leading-normal ${location.pathname.startsWith('/reports') ? 'bg-[#415972]' : 'hover:bg-[#415972]'}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Reports
                  </Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;